import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"

import Tooltip from "@/components/tooltip/Tooltip"
import type { ChartData, ToolTipContents } from "@/app/report/common/constructor"
import { VALUE_POSITION_TYPE } from "@/app/report/common/constants"
import { EXPORT_TYPE, type ExportFileFunctionType } from "@/app/report/common/type"
import { PDF } from "@/app/report/common/export/pdf"
import { exportSVG } from "@/app/report/common/export/export"
import { getDateTimeString, getLocaleString } from "@/app/report/common/tools"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { colors } from "@/components/colors"
import {
  coerceVerticalAxisTypes,
  drawVerticalPlayerChart,
  rowHeightFor,
} from "@/app/report/common/chart/verticalPlayerChart"
import { resolveGraphShowAxis } from "@/app/report/common/sections/graph"

import "./verticalPlayerChart.scss"

const EXPORT_WIDTH = 1200

/**
 * 리포트 화면의 Chart by Player 세로 차트 (일감 #185). MultipleChart(가로)와 같은 ref API(drawChart·exportFile)를
 * 제공해 ChartByPlayerSection 이 방향에 따라 바꿔 끼운다. 그리기는 공용 drawVerticalPlayerChart.
 * 화면은 한 묶음으로 길게 그리고(페이지 분할은 PDF·빌더 몫), 행 호버에 값 툴팁을 띄운다.
 */
function VerticalPlayerChart(
  { chartData, loading }: { chartData?: ChartData; loading: boolean },
  ref: React.ForwardedRef<{
    drawChart: () => void
    exportFile: (args: ExportFileFunctionType) => Promise<void>
  }>,
) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [tooltipTitle, setTooltipTitle] = useState("")
  const [tooltipValues, setTooltipValues] = useState<ToolTipContents[]>([])
  const [tooltipVisible, setTooltipVisible] = useState(false)

  useEffect(() => {
    const el = containerRef.current
    if (!el) {
      return
    }
    const update = () => setWidth(el.offsetWidth)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  // 템플릿의 값 표시 설정을 화면에도 반영한다. 2026-10-01 에 "화면은 툴팁으로"로 바꾸면서
  // 설정 선택기와 함께 끊겼는데, 설정만 되살리면 "설정했는데 화면에 안 나온다"가 된다 (2026-10-15).
  // 가로 차트(MultipleChart)와 같은 매핑을 쓴다
  const valuePositionType = useMemo<VALUE_POSITION_TYPE>(() => {
    const dataLabel = chartData?.settings?.dataLabel
    if (!dataLabel) {
      return VALUE_POSITION_TYPE.HIDE_VALUES
    }
    const mapping: Record<string, VALUE_POSITION_TYPE> = {
      hideValues: VALUE_POSITION_TYPE.HIDE_VALUES,
      abovePlots: VALUE_POSITION_TYPE.IN_CHART_EACH_POSITION,
      separateTable: VALUE_POSITION_TYPE.ABOVE,
    }

    return mapping[dataLabel] ?? VALUE_POSITION_TYPE.HIDE_VALUES
  }, [chartData?.settings?.dataLabel])

  const drawChart = useCallback(() => {
    const el = containerRef.current
    if (!el || !chartData || loading || width <= 0) {
      return
    }
    drawVerticalPlayerChart(el, {
      rows: chartData.data,
      axisY: coerceVerticalAxisTypes(chartData.axisY),
      width,
      valuePositionType,
      decimalPoint: chartData.settings.decimalPlace ?? null,
      showAxis: resolveGraphShowAxis(chartData.settings),
      onRowHover: (rowIndex) => {
        if (rowIndex == null) {
          setTooltipVisible(false)

          return
        }
        const row = chartData.data[rowIndex]
        setTooltipTitle(row.name)
        setTooltipValues(
          chartData.axisY.map((axis, index) => {
            const value = row.data[index]

            return {
              title: translateKey(axis.key) ?? axis.name,
              value:
                value == null
                  ? "-"
                  : `${getLocaleString(value, axis.decimalPoint)}${axis.unit ? ` ${axis.unit}` : ""}`,
              color: axis.color,
            } as ToolTipContents
          }),
        )
        setTooltipVisible(true)
      },
    })
  }, [chartData, loading, width, valuePositionType, translateKey])

  useEffect(() => {
    drawChart()
  }, [drawChart])

  // 내보내기 — 화면과 같은 SVG 를 1200px(× 배율)로 따로 그려 PNG/PDF 로. 범례는 SVG 위에 직접 얹는다
  const exportFile = useCallback(
    async ({
      exportType,
      magnification,
      reportType,
      chartAnnotation,
      decimalPoints,
    }: ExportFileFunctionType) => {
      if (!chartData) {
        return
      }
      const scratch = document.createElement("div")
      const svg = drawVerticalPlayerChart(scratch, {
        rows: chartData.data,
        axisY: coerceVerticalAxisTypes(chartData.axisY),
        showAxis: resolveGraphShowAxis(chartData.settings),
        width: EXPORT_WIDTH * magnification,
        rowHeight: rowHeightFor(coerceVerticalAxisTypes(chartData.axisY)) * magnification,
        valuePositionType: chartAnnotation,
        decimalPoint: decimalPoints,
        fontSize: 11 * magnification,
      })
      if (!svg) {
        return
      }
      const legendGap = 22 * magnification
      const viewBox = svg.viewBox.baseVal
      const totalHeight = viewBox.height + legendGap
      svg.setAttribute("viewBox", `0 0 ${viewBox.width} ${totalHeight}`)
      svg.setAttribute("height", String(totalHeight))
      const content = svg.querySelectorAll(":scope > g")
      content.forEach((g) => {
        const transform = g.getAttribute("transform") ?? ""
        g.setAttribute("transform", `translate(0, ${legendGap}) ${transform}`)
      })
      let x = 8 * magnification
      chartData.axisY.forEach((axis) => {
        const text = document.createElementNS("http://www.w3.org/2000/svg", "text")
        text.setAttribute("x", String(x))
        text.setAttribute("y", String(12 * magnification))
        text.setAttribute("dominant-baseline", "central")
        text.setAttribute("font-size", String(11 * magnification))
        text.setAttribute("fill", axis.color)
        text.textContent = `● ${translateKey(axis.key) ?? axis.name}`
        svg.insertBefore(text, svg.firstChild)
        x += (text.textContent.length * 7 + 16) * magnification
      })

      const fileName = `${getDateTimeString()}_${reportType}_report_${chartData.settings.title || "chart"}`
      if (exportType === EXPORT_TYPE.PDF) {
        const pdf = await PDF.create()
        await exportSVG(svg, fileName, colors.neutral.white, EXPORT_TYPE.PDF, pdf)
        pdf.save(`${fileName}.pdf`)

        return
      }
      await exportSVG(svg, fileName, colors.neutral.white, EXPORT_TYPE.PNG)
    },
    [chartData, translateKey],
  )

  useImperativeHandle(ref, () => ({ drawChart, exportFile }), [drawChart, exportFile])

  return (
    <Tooltip
      type="chart"
      label={tooltipTitle}
      title={
        <div className="bar-chart-tooltip">
          {tooltipValues.map((value, index) => (
            <div key={index} className="bar-chart-tooltip-item">
              {value.color && <span style={{ backgroundColor: value.color, color: value.color }} />}
              <p>{`${value.title || ""}${value.value ? " :" : ""}`}</p>
              {value.value && <p>{value.value}</p>}
            </div>
          ))}
        </div>
      }
      size="medium"
      open={tooltipVisible}>
      <div className="VerticalPlayerChart">
        <div className="VerticalPlayerChart__legend">
          {chartData?.axisY.map((axis) => (
            <div
              key={axis.key}
              className="VerticalPlayerChart__legend-item"
              style={{ color: axis.color }}>
              <span>●</span>
              <span>{translateKey(axis.key) ?? axis.name}</span>
            </div>
          ))}
        </div>
        {!loading && chartData && chartData.data.length === 0 && (
          <div className="VerticalPlayerChart__empty">{t("noData", "No data")}</div>
        )}
        <div ref={containerRef} className="VerticalPlayerChart__canvas" />
      </div>
    </Tooltip>
  )
}

export default forwardRef(VerticalPlayerChart)
